// THE DOCUMENTS TAB'S READS (spec 09 §4.2's E2-2a2 annotation, D-323 ruling
// 14): a customer's quotes and orders, newest first, twenty a page with Show
// more, through the two list routes the books already use
// (`GET /v1/quotes?customer_id=` and `GET /v1/orders?customer_id=`, both
// ordered `-created_at`).
//
// TWO CURSORS, ONE TIMELINE. The tab reads as one list of documents, newest
// first, and the server pages each kind on its own. `mergeTimeline` shows a
// document only once nothing older than it can still arrive from the other
// list: while a list has another page, its oldest loaded row is a horizon,
// and a row of either kind older than the newest horizon waits behind Show
// more. Show more then asks the next page of each list whose horizon is the
// one holding the timeline back, so a quote from March never paints above an
// order from April that the server had not yet sent. Pure and unit-tested.
//
// THE KEYS SIT UNDER THE BOOKS' PREFIXES (`["sales", "quotes", …]` and
// `["sales", "orders", …]`), so every write that invalidates the quote or
// order lists (a send, a cancel, an accept) reads the tab again too. Their
// third element is `"documents"`, which no book key uses, so the infinite
// shape never meets a book's single-page cache entry.

import { useInfiniteQuery } from "@tanstack/react-query";
import {
  fetchOrdersPage,
  fetchQuotesPage,
  type Quote as SalesQuote,
  type SalesOrder,
} from "./quote-queries";

/** Twenty a page with Show more (§4.2's E2-2a2 annotation). */
export const DOCUMENTS_PAGE = 20;

export const documentKeys = {
  quotes: (customerId: string) => ["sales", "quotes", "documents", customerId] as const,
  orders: (customerId: string) => ["sales", "orders", "documents", customerId] as const,
};

export function useCustomerQuoteDocuments(customerId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: documentKeys.quotes(customerId),
    enabled: enabled && customerId.length > 0,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      fetchQuotesPage(
        { customer_id: customerId, limit: DOCUMENTS_PAGE, order: "-created_at" },
        pageParam,
        signal,
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

export function useCustomerOrderDocuments(customerId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: documentKeys.orders(customerId),
    enabled: enabled && customerId.length > 0,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      fetchOrdersPage(
        { customer_id: customerId, limit: DOCUMENTS_PAGE, order: "-created_at" },
        pageParam,
        signal,
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** One row of the tab's timeline. */
export type DocumentRow =
  | { readonly kind: "quote"; readonly at: string; readonly doc: SalesQuote }
  | { readonly kind: "order"; readonly at: string; readonly doc: SalesOrder };

/** One list's loaded rows and whether the server holds more. */
export interface DocumentStream<T> {
  readonly items: readonly T[];
  readonly hasMore: boolean;
}

export interface MergedTimeline {
  /** The rows that may show now, newest first. */
  readonly rows: readonly DocumentRow[];
  /** Which lists Show more should ask for their next page: those whose
   * oldest loaded row is the horizon holding the timeline back. Empty when
   * nothing is held back and neither list has more. */
  readonly fetch: { readonly quotes: boolean; readonly orders: boolean };
}

function newestFirst(a: DocumentRow, b: DocumentRow): number {
  if (a.at !== b.at) return a.at < b.at ? 1 : -1;
  if (a.doc.id === b.doc.id) return 0;
  return a.doc.id < b.doc.id ? 1 : -1;
}

/**
 * The tab's one timeline from the two lists' loaded pages. A row shows when
 * it is at or after the horizon, the newest of the lists' oldest loaded rows
 * among the lists that have more pages. Timestamps compare as ISO strings,
 * which the wire serves in one UTC form.
 */
export function mergeTimeline(
  quotes: DocumentStream<SalesQuote>,
  orders: DocumentStream<SalesOrder>,
): MergedTimeline {
  const all: DocumentRow[] = [
    ...quotes.items.map((doc) => ({ kind: "quote" as const, at: doc.created_at, doc })),
    ...orders.items.map((doc) => ({ kind: "order" as const, at: doc.created_at, doc })),
  ].sort(newestFirst);
  // A list that says it has more and sent no row holds everything back: the
  // next row of either kind may come from it.
  if (
    (quotes.hasMore && quotes.items.length === 0) ||
    (orders.hasMore && orders.items.length === 0)
  ) {
    return {
      rows: [],
      fetch: {
        quotes: quotes.hasMore && quotes.items.length === 0,
        orders: orders.hasMore && orders.items.length === 0,
      },
    };
  }
  const quoteHorizon = quotes.hasMore ? quotes.items.at(-1)?.created_at : undefined;
  const orderHorizon = orders.hasMore ? orders.items.at(-1)?.created_at : undefined;
  const horizons = [quoteHorizon, orderHorizon].filter((h): h is string => h !== undefined);
  if (horizons.length === 0) {
    return { rows: all, fetch: { quotes: quotes.hasMore, orders: orders.hasMore } };
  }
  const horizon = horizons.reduce((a, b) => (a > b ? a : b));
  return {
    rows: all.filter((row) => row.at >= horizon),
    fetch: { quotes: quoteHorizon === horizon, orders: orderHorizon === horizon },
  };
}
